import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import {
  enumDocumentRelatedEntityType,
  enumDocumentRootEntityType,
  enumDocumentState,
  enumDocumentType,
} from '@app/data-layer/enums/documents';
import { ShipmentModel } from '@app/data-layer/models/common';
import { DocumentModel } from '@app/data-layer/models/documents';
import { EntrySummaryModel, InvoiceModel } from '@app/data-layer/models/entry-summaries';
import { TranslateService } from '@ngx-translate/core';
import { DocumentsService } from '@services/documents.service';
import { UtilsService } from '@services/utils/utils.service';
import { take } from 'rxjs/operators';

interface UploadDocumentModel {
  file: File;
  doc: DocumentModel;
  shipmentId: number;
}
interface DeleteDocumentModel {
  doc: DocumentModel;
  shipmentId: number;
}
interface StateUpdateDocumentModel {
  state: enumDocumentState;
  doc: DocumentModel;
  shipmentId: number;
}
interface AddOtherDocumentStateModel {
  type: enumDocumentType;
  shipmentId: number;
}
interface UploadNewDocumentModel {
  file: File;
  type: enumDocumentType;
  shipmentId: number;
}

@Component({
  selector: 'app-es-documents-bl-box',
  templateUrl: './es-documents-bl-box.component.html',
  styleUrls: ['./es-documents-bl-box.component.scss'],
})
export class EsDocumentsBlBoxComponent implements OnChanges {
  @Input() public index: number;
  @Input() public entrySummary: EntrySummaryModel;
  @Input() public shipment: ShipmentModel;
  @Input() public additionalDocumentsOptions: enumDocumentType[] = [];
  @Input() public entryDocuments: DocumentModel[] = [];
  @Input() public isShowSkipOcrScanControl: boolean = true;
  @Input() public skipOcrScanControl: UntypedFormControl;
  @Input() public isLoadingDocUploadFlags: Map<string, boolean> = new Map();
  @Input() public isBillOfLadingRequired: boolean;
  @Input() public isShowMultipleBlsHint: boolean = false;
  @Input() public floatingTitle: string;
  @Input() public isShowFloatingTitle: boolean = true;
  @Input() public isShowDeleteBlButton: boolean = true;
  @Input() public isShowDocRemove: boolean = true;
  @Input() public isShowDocPreview: boolean = false;
  @Input() public isShowDocDownload: boolean = false;
  @Input() public isShowDownloadAllDocs: boolean = false;
  @Input() public isShowLabelOverUploadedDate: boolean = false;
  @Input() public headerTitle: string = 'ENTRY_SUMMARY.BILL_OF_LADING_DOCS.TITLE_SINGLE';
  @Input() public isShowHeaderBorder: boolean = false;
  @Input() public theme: 'default' | 'highlighted' = 'default';
  @Input() public layoutClass: string = '';
  @Input() public isShowRequiredLaterBadge: boolean = false;

  @Output() public upload: EventEmitter<UploadDocumentModel> = new EventEmitter<UploadDocumentModel>();
  @Output() public preview: EventEmitter<DocumentModel> = new EventEmitter<DocumentModel>();
  @Output() public delete: EventEmitter<DeleteDocumentModel> = new EventEmitter<DeleteDocumentModel>();
  @Output() public deleteBl: EventEmitter<number> = new EventEmitter<number>();
  @Output() public stateUpdate: EventEmitter<StateUpdateDocumentModel> = new EventEmitter<StateUpdateDocumentModel>();
  @Output() public addOtherDocument: EventEmitter<AddOtherDocumentStateModel> = new EventEmitter<AddOtherDocumentStateModel>();
  @Output() public uploadNewDocument: EventEmitter<UploadNewDocumentModel> = new EventEmitter<UploadNewDocumentModel>();
  @Output() public inputClick: EventEmitter<void> = new EventEmitter<void>();

  constructor(private utilsService: UtilsService, private documentsService: DocumentsService, private translateService: TranslateService) {}

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.entryDocuments) {
      // 1. Reset loading state on the card that triggered the upload
      if (this.loadingLocalDocId != null) {
        const loadingId = this.loadingLocalDocId;
        this.loadingLocalDocId = null;
        this.localAdditionalDocuments = this.localAdditionalDocuments.map((d) => (d.id === loadingId ? { ...d } : d));
      }

      // 2. Detect deletes via REAL doc counts (docs with createdDate).
      //    Placeholders don't have createdDate, so placeholder restoration won't mask deletions.
      //    On delete: remove one local card per decrease (prefer uploaded first, then non-uploaded).
      if (this.localAdditionalDocuments.length > 0 && this.previousRealCountByType.size > 0) {
        const typesToCheck = new Set<number>();
        this.localAdditionalDocuments.forEach((d) => typesToCheck.add(d.type));

        const idsToRemove: number[] = [];
        typesToCheck.forEach((type) => {
          const prevRealCount = this.previousRealCountByType.get(type) || 0;
          const currRealCount = this.entryDocuments?.filter((d) => d.type === type && !!d.createdDate).length || 0;
          if (currRealCount < prevRealCount) {
            const decrease = prevRealCount - currRealCount;
            // Prefer removing uploaded local cards first, then non-uploaded
            const localOfType = this.localAdditionalDocuments.filter((d) => d.type === type);
            const sortedCandidates = [
              ...localOfType.filter((d) => this.uploadedLocalDocIds.has(d.id)),
              ...localOfType.filter((d) => !this.uploadedLocalDocIds.has(d.id)),
            ];
            for (let i = 0; i < Math.min(decrease, sortedCandidates.length); i++) {
              idsToRemove.push(sortedCandidates[i].id);
            }
          }
        });

        if (idsToRemove.length > 0) {
          idsToRemove.forEach((id) => {
            this.uploadedLocalDocIds.delete(id);
            this.initialTypeCountsForLocalDocs.delete(id);
          });
          this.localAdditionalDocuments = this.localAdditionalDocuments.filter((d) => !idsToRemove.includes(d.id));
        }
      }

      // 3. Remove absorbed uploaded cards — cards whose uploads are now reflected in server docs.
      if (this.localAdditionalDocuments.length > 0) {
        const typeStats = new Map<number, { minInitial: number; uploadedCount: number }>();
        this.localAdditionalDocuments.forEach((d) => {
          if (this.uploadedLocalDocIds.has(d.id)) {
            const initial = this.initialTypeCountsForLocalDocs.get(d.id) || 0;
            const stats = typeStats.get(d.type);
            if (stats) {
              stats.uploadedCount++;
              stats.minInitial = Math.min(stats.minInitial, initial);
            } else {
              typeStats.set(d.type, { minInitial: initial, uploadedCount: 1 });
            }
          }
        });

        const absorbedCountByType = new Map<number, number>();
        const absorbedIds: number[] = [];
        this.localAdditionalDocuments.forEach((d) => {
          if (this.uploadedLocalDocIds.has(d.id)) {
            const stats = typeStats.get(d.type);
            if (stats) {
              const currentRealCount = this.entryDocuments?.filter((ed) => ed.type === d.type && !!ed.createdDate).length || 0;
              const surplus = Math.max(currentRealCount - stats.minInitial, 0);
              const maxAbsorb = Math.min(surplus, stats.uploadedCount);
              const alreadyAbsorbed = absorbedCountByType.get(d.type) || 0;
              if (alreadyAbsorbed < maxAbsorb) {
                absorbedIds.push(d.id);
                absorbedCountByType.set(d.type, alreadyAbsorbed + 1);
              }
            }
          }
        });

        if (absorbedIds.length > 0) {
          absorbedIds.forEach((id) => {
            this.uploadedLocalDocIds.delete(id);
            this.initialTypeCountsForLocalDocs.delete(id);
          });
          this.localAdditionalDocuments = this.localAdditionalDocuments.filter((d) => !absorbedIds.includes(d.id));
        }
      }

      // 4. Update previous REAL doc counts (only docs with createdDate)
      const newRealCounts = new Map<number, number>();
      this.entryDocuments?.forEach((d) => {
        if (d.createdDate) {
          newRealCounts.set(d.type, (newRealCounts.get(d.type) || 0) + 1);
        }
      });
      this.previousRealCountByType = newRealCounts;
    }
  }

  public enumDocumentType: typeof enumDocumentType = enumDocumentType;
  public localAdditionalDocuments: DocumentModel[] = [];
  private additionalDocCounter: number = -100;
  private uploadedLocalDocIds: Set<number> = new Set();
  private initialTypeCountsForLocalDocs: Map<number, number> = new Map();
  private loadingLocalDocId: number | null = null;
  private previousRealCountByType: Map<number, number> = new Map();

  public document(doc: DocumentModel, shipmentId: number): DocumentModel {
    if (!this.entryDocuments) {
      return;
    }

    return this.entryDocuments?.find((document: DocumentModel) => {
      let invoice: InvoiceModel;
      if (doc.type === enumDocumentType.INVOICE) {
        const currentShipment = this.entrySummary?.shipments?.find((sh) => sh.id === shipmentId);
        const invoices = currentShipment?.invoices;
        invoice = invoices?.find((i) => i.id === doc.relatedEntityId);
      }
      return document.id === doc.id && doc.relatedEntityId === (!!invoice ? invoice.id : shipmentId);
    });
  }

  public documentName(doc: DocumentModel, docNumber?: number): string {
    const currentShipmentOfTypeDocs = this.entryDocuments?.filter((cdotd) => cdotd.type === doc.type);
    const indexOfCurrentShipmentOfTypeDoc = currentShipmentOfTypeDocs.findIndex((cdotd) => cdotd.id === doc.id);
    const documentTypeName = enumDocumentType[doc.type];
    const blTypeDocTitlePath = `ENTRY_SUMMARY.BILL_OF_LADING_DOCS.${documentTypeName}.TITLE`;
    const documentTypeTitlePath = `DOCUMENT_TYPE.${documentTypeName}`;

    if (doc.type === enumDocumentType.BILL_OF_LADING) {
      return this.translateService.instant(blTypeDocTitlePath) + (this.entrySummary?.shipments?.length > 1 ? ` #${docNumber}` : '');
    }

    const isBlDocTranslatedTextExist = !!this.utilsService.getTextAtPath(blTypeDocTitlePath);
    const translatedTitle = isBlDocTranslatedTextExist
      ? this.translateService.instant(blTypeDocTitlePath)
      : this.translateService.instant(documentTypeTitlePath);

    const documentName = !!indexOfCurrentShipmentOfTypeDoc ? translatedTitle + ` #${indexOfCurrentShipmentOfTypeDoc + 1}` : translatedTitle;

    return documentName;
  }

  public additionalDocumentName(doc: DocumentModel): string {
    const documentTypeName = enumDocumentType[doc.type];
    const blTypeDocTitlePath = `ENTRY_SUMMARY.BILL_OF_LADING_DOCS.${documentTypeName}.TITLE`;
    const documentTypeTitlePath = `DOCUMENT_TYPE.${documentTypeName}`;

    const isBlDocTranslatedTextExist = !!this.utilsService.getTextAtPath(blTypeDocTitlePath);
    const translatedTitle = isBlDocTranslatedTextExist
      ? this.translateService.instant(blTypeDocTitlePath)
      : this.translateService.instant(documentTypeTitlePath);

    const entryDocsOfSameType = this.entryDocuments?.filter((d) => d.type === doc.type).length || 0;
    const localDocsOfSameType = this.visibleLocalAdditionalDocuments.filter((d) => d.type === doc.type);
    const localIndex = localDocsOfSameType.findIndex((d) => d.id === doc.id);
    const overallIndex = entryDocsOfSameType + localIndex;

    return overallIndex > 0 ? `${translatedTitle} #${overallIndex + 1}` : translatedTitle;
  }

  public documentHint(docType: enumDocumentType): string {
    const documentTypeName = enumDocumentType[docType];
    return `ENTRY_SUMMARY.BILL_OF_LADING_DOCS.${documentTypeName}.DESCRIPTION`;
  }

  public isRequired(docType: enumDocumentType): boolean {
    const requiredDocTypes = [enumDocumentType.INVOICE];
    if (this.isBillOfLadingRequired) {
      requiredDocTypes.push(enumDocumentType.BILL_OF_LADING);
    }
    return requiredDocTypes.includes(docType);
  }

  public isShowDeferredRequiredTag(docType: enumDocumentType): boolean {
    return this.isShowRequiredLaterBadge && [enumDocumentType.ARRIVAL_NOTICE].includes(docType);
  }

  public isShowOptionalTag(docType: enumDocumentType): boolean {
    const optionalDocTypes = [enumDocumentType.PACKING_LIST];
    if (!this.isBillOfLadingRequired) {
      optionalDocTypes.push(enumDocumentType.BILL_OF_LADING);
    }
    return optionalDocTypes.includes(docType);
  }

  public onUploadDocument(file: File, doc: DocumentModel, shipmentId: number): void {
    this.upload.emit({ file, doc, shipmentId });
  }

  public onDeleteDocument(doc: DocumentModel, shipmentId: number): void {
    this.delete.emit({ doc, shipmentId });
  }

  public onPreviewDocument(doc: DocumentModel) {
    this.preview.emit(doc);
  }

  public onDeleteBl(shipmentId: number): void {
    this.deleteBl.emit(shipmentId);
  }

  public onUpdateDocumentState(state: enumDocumentState, doc: DocumentModel, shipmentId: number): void {
    this.stateUpdate.emit({ state, doc, shipmentId });
  }

  public get visibleLocalAdditionalDocuments(): DocumentModel[] {
    return this.localAdditionalDocuments;
  }

  public onAddOtherDocument(type: enumDocumentType, shipmentId: number): void {
    const newDoc: DocumentModel = {
      id: this.additionalDocCounter--,
      type,
      url: null,
      relatedEntityType: enumDocumentRelatedEntityType.SHIPMENT,
      relatedEntityId: shipmentId,
      rootEntityType: enumDocumentRootEntityType.ENTRY_SUMMARY,
      rootEntityId: this.entrySummary?.id,
      fileName: null,
      createdDate: null,
      state: null,
    };
    const currentTypeCount = this.entryDocuments?.filter((d) => d.type === type && !!d.createdDate).length || 0;
    this.initialTypeCountsForLocalDocs.set(newDoc.id, currentTypeCount);
    this.localAdditionalDocuments = [...this.localAdditionalDocuments, newDoc];
  }

  public onUploadAdditionalDocument(file: File, doc: DocumentModel, shipmentId: number): void {
    this.uploadNewDocument.emit({ file, type: doc.type, shipmentId });
    this.uploadedLocalDocIds.add(doc.id);
    this.loadingLocalDocId = doc.id;
  }

  public onRemoveAdditionalSlot(doc: DocumentModel): void {
    this.uploadedLocalDocIds.delete(doc.id);
    this.initialTypeCountsForLocalDocs.delete(doc.id);
    this.localAdditionalDocuments = this.localAdditionalDocuments.filter((d) => d.id !== doc.id);
  }

  public onDownloadAllFiles(): void {
    const attachedDocumentsIds = this.entryDocuments
      .filter((item: DocumentModel) => !!item.createdDate)
      .map((item: DocumentModel) => item.id);
    this.documentsService
      .getArchive(this.entrySummary.referenceNumber, attachedDocumentsIds)
      .pipe(take(1))
      .subscribe((file: Blob) => {
        this.documentsService.downloadBlobFile(file, this.documentsService.formatZipFileName(this.entrySummary.referenceNumber));
      });
  }

  public onInputClick() {
    this.inputClick.emit();
  }

  public get sectionClassName(): string {
    if (this.isShowFloatingTitle && !(!!this.index && this.isShowDeleteBlButton)) {
      return 'top-padding-50';
    } else if (this.isShowFloatingTitle && !!this.index && this.isShowDeleteBlButton) {
      return 'top-padding-55';
    }
  }
}
